import { TOKEN_METADATA_BY_SYMBOL } from "@constants/metadata";
import type { Market } from "@src/api";
import { memo } from "react";
import { FavoriteMarketItem } from "./FavoriteMarketItem";
import { EmptyText, FavoritesScroll } from "./styles";
import type { DisplayMode } from "./types";

interface FavoriteMarketsListProps {
  addresses: string[];
  allMarkets: Market[];
  displayMode: DisplayMode;
  onMarketClick: (m: Market) => void;
}

// Non-observer list component - only re-renders when favorite addresses change
export const FavoriteMarketsList = memo(
  ({ addresses, allMarkets, displayMode, onMarketClick }: FavoriteMarketsListProps) => {
    // Show empty state if no addresses saved
    if (addresses.length === 0) {
      return <EmptyText data-testid="favorite-markets:empty">Pin Your Favorite Markets</EmptyText>;
    }

    // Show empty state if markets haven't loaded yet
    if (allMarkets.length === 0) {
      return <EmptyText data-testid="favorite-markets:empty">Pin Your Favorite Markets</EmptyText>;
    }

    // Get static market info - recalculates only when addresses change
    const favoriteMarketsInfo = addresses
      .map((address) => {
        const market = allMarkets.find((m) => m.addr === address);
        if (!market) return null;

        return {
          address,
          baseSymbol: market.base_symbol,
          symbol: market.symbol,
          token: TOKEN_METADATA_BY_SYMBOL[market.base_symbol],
        };
      })
      .filter((info): info is NonNullable<typeof info> => info !== null);

    // Show empty state if no valid markets found (invalid/old addresses from localStorage)
    if (favoriteMarketsInfo.length === 0) {
      return <EmptyText data-testid="favorite-markets:empty">Pin Your Favorite Markets</EmptyText>;
    }

    return (
      <FavoritesScroll data-testid="favorite-markets:list">
        {favoriteMarketsInfo.map((info) => (
          <FavoriteMarketItem
            key={info.address}
            marketAddress={info.address}
            baseSymbol={info.baseSymbol}
            symbol={info.symbol}
            token={info.token}
            displayMode={displayMode}
            onClick={onMarketClick}
          />
        ))}
      </FavoritesScroll>
    );
  },
  (prevProps, nextProps) => {
    // Allow initial render when markets load for the first time
    if (prevProps.allMarkets.length === 0 && nextProps.allMarkets.length > 0) {
      return false; // Force re-render when markets first load
    }

    // Only re-render if addresses or displayMode change (ignore subsequent allMarkets updates)
    return (
      prevProps.displayMode === nextProps.displayMode &&
      prevProps.addresses.length === nextProps.addresses.length &&
      prevProps.addresses.every((addr, i) => addr === nextProps.addresses[i])
    );
  },
);
